import React, { useState, useEffect } from 'react'
import { Card, message, Modal, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import AccountDetails from '../FollowUpManager/AccountDetails/AccountDetails'
import SummaryStatistics from './components/SummaryStatistics'
import SearchAndActions from './components/SearchAndActions'
import PatientAccountsTable from './components/PatientAccountsTable'
import { getPatientLedgerSummary, getFollowUpAccounts } from '../../backend-integration/api'
import { readPatients } from '../../../Admin/patients/utils/utils'
import Agilite from 'agilite'
import store from '../../../../store'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

const { Text } = Typography

const PatientAccountsList = () => {
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [showAccountDetails, setShowAccountDetails] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [totals, setTotals] = useState({
    totalPatients: 0,
    totalOutstanding: 0,
    medicalAidOutstanding: 0,
    compensationOutstanding: 0,
    patientOutstanding: 0
  })
  const navigate = useNavigate()

  useEffect(() => {
    loadPatientAccounts()
    loadTotalOutstanding()
  }, [])

  const loadTotalOutstanding = async () => {
    try {
      const allAccounts = await getFollowUpAccounts({}, 1, 1000)

      let totalOutstanding = 0
      let totalMedicalAidOutstanding = 0
      let totalCompensationOutstanding = 0
      let totalPatientOutstanding = 0
      let uniquePatientCount = 0

      if (allAccounts && allAccounts.length > 0) {
        const processedBillingIds = new Set()

        for (const account of allAccounts) {
          const billingId = account.billingId

          if (!billingId || processedBillingIds.has(billingId)) continue
          processedBillingIds.add(billingId)
          uniquePatientCount++

          try {
            const summary = await getPatientLedgerSummary(billingId)

            if (summary && summary.currentBalance > 0) {
              totalOutstanding += summary.currentBalance
              totalMedicalAidOutstanding += summary.medicalAidBalance || 0
              totalCompensationOutstanding += summary.compensationBalance || 0
              totalPatientOutstanding += summary.patientBalance || 0
            }
          } catch (err) {
            console.warn('Error getting ledger summary for billingId:', billingId, err)
            // Still count the patient even if ledger summary fails
            totalMedicalAidOutstanding += account.medicalAidOutstanding || 0
            totalCompensationOutstanding += account.compensationOutstanding || 0
            totalPatientOutstanding += account.patientOutstanding || 0
          }
        }
      }

      setTotals({
        totalPatients: uniquePatientCount,
        totalOutstanding,
        medicalAidOutstanding: totalMedicalAidOutstanding,
        compensationOutstanding: totalCompensationOutstanding,
        patientOutstanding: totalPatientOutstanding
      })
    } catch (error) {
      console.error('Failed to load total outstanding:', error)
    }
  }

  const loadPatientAccounts = async (search = searchText, resetPage = false) => {
    setLoading(true)
    try {
      const filter = {
        billingId: { $exists: true, $ne: null },
        'extraData.role.type': 'patient',
        entityRef: store.getState().core.entity._id
      }

      if (search) {
        const searchTerm = search.trim()
        const searchWords = searchTerm.split(/\s+/)

        filter.$or = [
          // Search in firstName
          { firstName: { $regex: searchTerm, $options: 'i' } },
          // Search in lastName
          { lastName: { $regex: searchTerm, $options: 'i' } },
          // Search in ID number
          { idNo: { $regex: searchTerm, $options: 'i' } }
        ]

        // If there are multiple words, search for full name combinations
        if (searchWords.length >= 2) {
          filter.$or.push(
            // First word in firstName, second word in lastName
            {
              $and: [
                { firstName: { $regex: searchWords[0], $options: 'i' } },
                { lastName: { $regex: searchWords[1], $options: 'i' } }
              ]
            },
            // First word in lastName, second word in firstName (reverse order)
            {
              $and: [
                { lastName: { $regex: searchWords[0], $options: 'i' } },
                { firstName: { $regex: searchWords[1], $options: 'i' } }
              ]
            }
          )

          // If more than 2 words, try to match all combinations
          if (searchWords.length >= 3) {
            const firstNamePattern = searchWords.slice(0, -1).join('.*')
            const lastNamePattern = searchWords[searchWords.length - 1]

            filter.$or.push({
              $and: [
                { firstName: { $regex: firstNamePattern, $options: 'i' } },
                { lastName: { $regex: lastNamePattern, $options: 'i' } }
              ]
            })
          }
        }

        // Search for billing ID (only exact numeric matches since billingId is stored as number)
        const searchNumber = parseInt(searchTerm)
        if (!isNaN(searchNumber)) {
          filter.$or.push({ billingId: searchNumber })
        }
      }

      const page = resetPage ? 1 : pagination.current
      const pageLimit = pagination.pageSize

      const currentEntityId = store.getState().core.entity._id
      console.log('🔍 [PATIENT-COUNT] Current entity ID:', currentEntityId)

      const countFilter = {
        billingId: { $exists: true, $ne: null },
        'extraData.role.type': 'patient',
        entityRef: currentEntityId
      }
      console.log('🔍 [PATIENT-COUNT] Count filter:', countFilter)

      const countResponse = await agilite.Connectors.execute('auth_users', 'count', {
        filter: JSON.stringify(countFilter)
      })
      // Extract the actual count number from the response
      console.log('🔍 [PATIENT-COUNT] Count response:', countResponse)
      console.log('🔍 [PATIENT-COUNT] Count response data:', countResponse.data)
      console.log('🔍 [PATIENT-COUNT] Count response data type:', typeof countResponse.data)

      const totalCount =
        typeof countResponse.data === 'number'
          ? countResponse.data
          : countResponse.data?.result || countResponse.data?.count || countResponse.data?.total || 0

      console.log('🔍 [PATIENT-COUNT] Final totalCount:', totalCount, typeof totalCount)

      const response = await readPatients(filter, {}, null, page, pageLimit)
      const patientsData = Array.isArray(response) ? response : response.docs || []

      const patientIds = patientsData.map((p) => p._id)
      const followUpAccounts = await getFollowUpAccounts(
        {
          patientId: { $in: patientIds }
        },
        1,
        1000
      )
      const accountsMap = new Map(followUpAccounts.map((acc) => [acc.patientId, acc]))

      const processedPatients = await Promise.all(
        patientsData.map(async (patient) => {
          const followUpAccount = accountsMap.get(patient._id)
          const billingId = followUpAccount?.billingId || patient.billingId
          let ledgerSummary = null
          if (billingId) {
            try {
              ledgerSummary = await getPatientLedgerSummary(billingId)
            } catch (err) {
              console.warn('Error getting ledger summary for patient:', patient._id, err)
            }
          }

          const totalOutstanding = ledgerSummary?.currentBalance ?? followUpAccount?.outstandingAmount ?? 0
          const medicalAidOutstanding = ledgerSummary?.medicalAidBalance ?? followUpAccount?.medicalAidOutstanding ?? 0
          const compensationOutstanding =
            ledgerSummary?.compensationBalance ?? followUpAccount?.compensationOutstanding ?? 0
          const patientOutstanding = ledgerSummary?.patientBalance ?? followUpAccount?.patientOutstanding ?? 0

          return {
            key: patient._id,
            _id: patient._id,
            billingId: patient.billingId,
            name: `${patient.firstName} ${patient.lastName}`,
            firstName: patient.firstName,
            lastName: patient.lastName,
            idNo: patient.idNo || 'N/A',
            fileNo: patient.fileNo || followUpAccount?.fileNo || 'N/A',
            medicalAid: patient.medicalAid?.name || 'Private',
            phoneNumber: patient.phoneNumber || 'N/A',
            email: patient.email || 'N/A',
            totalOutstanding,
            medicalAidOutstanding,
            compensationOutstanding,
            patientOutstanding,
            lastInvoiceDate: followUpAccount?.lastInvoiceDate,
            lastPaymentDate: followUpAccount?.lastPaymentDate,
            daysOutstanding: followUpAccount?.daysOutstanding || 0,
            accountStatus: followUpAccount?.status || 'active',
            hasFollowUpAccount: !!followUpAccount,
            followUpAccount
          }
        })
      )

      console.log('🔍 [PATIENT-COUNT] Setting totalPatients to:', totalCount)
      setTotals((prev) => {
        const newTotals = {
          ...prev,
          totalPatients: totalCount
        }
        console.log('🔍 [PATIENT-COUNT] New totals state:', newTotals)
        return newTotals
      })

      setPatients(processedPatients)

      setPagination((prev) => ({
        ...prev,
        total: totalCount,
        current: resetPage ? 1 : prev.current
      }))
    } catch (error) {
      console.error('Failed to load patient accounts:', error)
      message.error('Failed to load patient accounts')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    setSearchText(value)
    setPagination((prev) => ({ ...prev, current: 1 }))
    loadPatientAccounts(value, true)
  }

  const handleViewAccount = async (patient) => {
    setSelectedAccount({
      ...patient.followUpAccount,
      billingId: patient.billingId,
      patientId: patient._id,
      patientName: patient.name,
      fileNo: patient.fileNo,
      medicalAid: patient.medicalAid,
      phoneNumber: patient.phoneNumber,
      email: patient.email,
      outstandingAmount: patient.totalOutstanding,
      patientOutstanding: patient.patientOutstanding,
      medicalAidOutstanding: patient.medicalAidOutstanding,
      compensationOutstanding: patient.compensationOutstanding,
      daysOutstanding: patient.daysOutstanding,
      status:
        patient.followUpAccount?.status ||
        (patient.patientOutstanding > 0
          ? 'awaiting_payment_patient'
          : patient.medicalAidOutstanding > 0
          ? 'awaiting_payment_aid'
          : patient.compensationOutstanding > 0
          ? 'awaiting_payment_compensation'
          : 'completed')
    })
    setShowAccountDetails(true)
  }

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }))
    loadPatientAccounts(searchText, false)
  }

  return (
    <>
      <Card>
        <SummaryStatistics totals={totals} />
        <SearchAndActions
          onSearch={handleSearch}
          onRefresh={() => loadPatientAccounts(searchText, false)}
          onNewPatient={() => navigate('/patients/new')}
          loading={loading}
        />
        <PatientAccountsTable
          patients={patients}
          loading={loading}
          pagination={pagination}
          onViewAccount={handleViewAccount}
          onNewInvoice={(record) => navigate('/invoices/new', { state: { patientId: record._id } })}
          onPageChange={handlePageChange}
        />
      </Card>

      {showAccountDetails && selectedAccount && (
        <Modal
          open={showAccountDetails}
          onCancel={() => {
            setShowAccountDetails(false)
            setSelectedAccount(null)
            loadPatientAccounts(searchText)
          }}
          width='95%'
          style={{ top: 20 }}
          footer={null}
          destroyOnClose
        >
          <AccountDetails
            accountData={selectedAccount}
            onClose={() => {
              setShowAccountDetails(false)
              setSelectedAccount(null)
              loadPatientAccounts(searchText)
            }}
          />
        </Modal>
      )}
    </>
  )
}

export default PatientAccountsList
